import {
  panels,
  components,
  multilingual,
  selection,
  domMeasurements,
  stateMapperArgsSelectors,
  sections as sectionsState,
} from '#packages/stateManagement';
import constants from '#packages/constants';
import {
  biLogger,
  sections as sectionsUtils,
  fedopsLogger,
} from '#packages/util';
import {
  addSectionButton,
  sectionEmptyStateClick,
  sectionShowEmptyState,
} from '@wix/bi-logger-editor/v2';
import { ErrorReporter } from '@wix/editor-error-reporter';
import {
  UNSCALED_DROPZONE_HEIGHT,
  DEFAULT_SITE_SCALE,
} from '#packages/zoomMode';
import { selectors as sectionsSelectors } from '#packages/sections';
import {
  SwitchLayoutApiKey,
  EditorRestrictionsApiKey,
  AddPresetApiKey,
} from '#packages/apis';
import { getIsAddSectionButtonOverlapsComponent } from './utils';
import { SelectSectionBiOrigin, ButtonAlignment } from './constants';
import { getSectionStageActionsSkin } from '../StageActions/StageActions.skins';

import { type SectionBoxOwnProps, SectionBoxType } from './SectionBox';

const {
  mouseSels: { isDraggingSel, isResizingSel },
} = stateMapperArgsSelectors;

import type {
  Dispatch,
  MapDispatchToProps,
  MapStateToProps,
  ThunkAction,
} from 'types/redux';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef } from 'types/documentServices';
import type { SectionLikeRect } from '../types';
import type { UISkin } from '../skins/skins.types';

type InteractionName = 'view' | 'hover' | 'click';

type ShowEmptyState = 'hover' | 'click';

type EmptyStateButtonClick =
  | 'add-section'
  | 'add-element'
  | 'add-designed-section'
  | 'add-ai-generated-section';

type SectionsSelection = ReturnType<typeof getSelection>;

export interface SectionBoxStateProps {
  canSelectSection: boolean;
  isLandingPage: boolean;
  hasSectionAbove: boolean;
  isConnectedToDataBinding: boolean;
  isLeftShrinkedStageZoomOut: boolean;
  isFocused: boolean;
  isResizing: boolean;
  isDragging: boolean;
  isMultilingual: boolean;
  shouldShowEmptyState: boolean;
  isMobileEditor: boolean;
  sectionRef: CompRef;
  sectionType: sectionsUtils.constants.SectionTypes;
  sectionRect: SectionLikeRect;
  siteScale: number;
  stageXOffset: number;
  shouldDisplayResizeHandle: boolean;
  shouldHideLabels: boolean;
  isSectionBothSelectedAndHovered: boolean;
  isBlankSection: boolean;
  corvidName: string;
  shouldDisplayPreLoader: boolean;
  isInSwitchLayoutMode: boolean;
  isOverlayVisible: boolean;
  areContainerPointerEventsEnabled: boolean;
  stageActionsSkin: UISkin;
}

const addSectionInteraction = fedopsLogger.mapInteraction(
  fedopsLogger.INTERACTIONS.CLASSIC_SECTIONS.ADD_SECTION,
);

const isMultilingual = (editorAPI: EditorAPI) =>
  editorAPI.language.multilingual.isEnabled() &&
  !multilingual.services.utils.currentIsOriginal(editorAPI);

const SECTION_EMPTY_STATE_MIN_HEIGHT = 190;

const getSelection = (editorAPI: EditorAPI, ownProps: SectionBoxOwnProps) => {
  const hoveredSection = editorAPI.sections.getHoveredSection();
  const selectedSection = editorAPI.sections.getSelectedSection();
  const focusedSection = editorAPI.sections.getFocusedSection();
  const hoveredHeaderFooter = editorAPI.sections.getHoveredHeaderFooter();
  const selectedHeaderFooter = editorAPI.sections.getSelectedHeaderFooter();
  const focusedHeaderFooter = editorAPI.sections.getFocusedHeaderFooter();
  const isSelectedBox = ownProps.type === SectionBoxType.Selected;
  const isHoveredBox = ownProps.type === SectionBoxType.Hovered;
  const bothSectionBoxesForSameSection = editorAPI.utils.isSameRef(
    selectedSection || focusedSection,
    hoveredSection,
  );
  const isSectionBothSelectedAndHovered = editorAPI.utils.isSameRef(
    selectedSection,
    hoveredSection,
  );
  const sectionRef = isSelectedBox
    ? selectedSection || focusedSection
    : hoveredSection;

  return {
    selectedSection,
    hoveredSection,
    focusedSection,
    hoveredHeaderFooter,
    selectedHeaderFooter,
    focusedHeaderFooter,
    isSelectedBox,
    isHoveredBox,
    sectionRef,
    bothSectionBoxesForSameSection,
    isSectionBothSelectedAndHovered,
  };
};

export const getSectionHeight = (editorAPI: EditorAPI, compRef: CompRef) =>
  editorAPI.components.responsiveLayout.runtime.measure.getNonRotatedBoundingBox(
    compRef,
  ).height;

const shouldShowEmptyState = (
  editorAPI: EditorAPI,
  compRef: CompRef,
  isSelectedBox: boolean,
  isBlankSection: boolean,
  bothSelectedAndHovered: boolean,
  isLeftShrinkedStageZoomOut: boolean,
) => {
  const switchLayoutAPI = editorAPI.host.getAPI(SwitchLayoutApiKey);

  const isSmallSection =
    getSectionHeight(editorAPI, compRef) < SECTION_EMPTY_STATE_MIN_HEIGHT;

  if (
    isSmallSection ||
    !isBlankSection ||
    editorAPI.isMobileEditor() ||
    isMultilingual(editorAPI) ||
    isLeftShrinkedStageZoomOut ||
    switchLayoutAPI.isSwitchLayoutSelecting()
  ) {
    return false;
  }

  return bothSelectedAndHovered ? isSelectedBox : true;
};

const shouldShowStageActions = (
  editorAPI: EditorAPI,
  {
    sectionRef,
    hoveredHeaderFooter,
    hoveredSection,
    isSelectedBox,
    isSectionBothSelectedAndHovered,
  }: SectionsSelection,
) => {
  if (editorAPI.sections.isBlankSection(sectionRef)) return false;
  if (isSectionBothSelectedAndHovered) {
    return (
      isSelectedBox &&
      editorAPI.sections.isFirstTimeParentSectionLikeFocused(sectionRef)
    );
  }

  if (isSelectedBox) {
    return !hoveredHeaderFooter && !hoveredSection;
  }

  return true;
};

export const mapStateToProps: MapStateToProps<
  SectionBoxStateProps,
  SectionBoxOwnProps
> = (stateMapperArgs, ownProps) => {
  const { editorAPI, state, dsRead } = stateMapperArgs;

  const sectionSelection = getSelection(editorAPI, ownProps);

  const {
    selectedSection,
    hoveredSection,
    focusedSection,
    isSelectedBox,
    isHoveredBox,
    sectionRef,
    bothSectionBoxesForSameSection,
    isSectionBothSelectedAndHovered,
  } = sectionSelection;

  const siteScale = editorAPI.getSiteScale();
  const stageXOffset = editorAPI.zoomMode.getStageXOffset();
  const sectionLayout =
    editorAPI.components.layout.getRelativeToScreen(sectionRef);
  const isStageZoomOut = editorAPI.zoomMode.isStageZoomMode();
  const isLeftShrinkedStageZoomOut =
    editorAPI.zoomMode.isLeftShrinkedStageZoomOutActive();
  const isDevMode = editorAPI.developerMode.isEnabled();
  const corvidName = isDevMode
    ? components.selectors.getNickname(sectionRef, dsRead)
    : null;
  const isConnectedToDataBinding =
    selection.selectors.isSelectedCompConnectedToDataBindingController(
      state,
      dsRead,
    );
  const isDragging = isDraggingSel(stateMapperArgs);

  const translateYValue = sectionsUtils.getSectionLikeTranslateY(
    editorAPI,
    sectionRef,
  );

  const sectionRect = {
    width: sectionLayout.width,
    height: sectionLayout.height,
    top: sectionLayout.y + translateYValue,
    bottom: sectionLayout.y + sectionLayout.height + translateYValue,
  };

  const switchLayoutAPI = editorAPI.host.getAPI(SwitchLayoutApiKey);
  const isInSwitchLayoutMode = switchLayoutAPI.isInSwitchLayoutMode();
  const shouldDisplayPreLoader =
    isInSwitchLayoutMode &&
    switchLayoutAPI.isSwitchLayoutSelecting() &&
    switchLayoutAPI.getComponentForSwitchLayout().id ===
      sectionRef.id; /* TODO: check that this is the selected SectionBox (&& isSelectedBox) - now always returns false when zoom out */

  const isLandingPage = editorAPI.pages.isLandingPage(
    editorAPI.pages.getFocusedPageId(),
  );

  const isFocused = isSelectedBox && focusedSection && !selectedSection;

  const getIsOverlayVisible = () => {
    if (!isHoveredBox) return false;

    const isSectionActionsBarHovered =
      sectionsState.selectors.getIsSectionActionsBarHovered(state);

    return !bothSectionBoxesForSameSection || isSectionActionsBarHovered;
  };

  const isOverlayVisible = getIsOverlayVisible();

  const editorRestrictionsApi = editorAPI.host.getAPI(EditorRestrictionsApiKey);
  const areContainerPointerEventsEnabled =
    editorRestrictionsApi.allowed(
      'sections_container-pointer-events.interactive',
    ) || isStageZoomOut;

  const stageActionsSkin = shouldShowStageActions(editorAPI, sectionSelection)
    ? getSectionStageActionsSkin(editorAPI, sectionRef, {
        selectedSectionLike: selectedSection,
        hoveredSectionLike: hoveredSection,
        focusedSectionLike: focusedSection,
      })
    : undefined;

  const isBlankSection = editorAPI.sections.isBlankSection(sectionRef);

  return {
    canSelectSection: sectionsSelectors.canSelectSectionLike(stateMapperArgs),
    isLandingPage,
    hasSectionAbove: Boolean(editorAPI.sections.getSectionAbove(sectionRef)),
    isConnectedToDataBinding,
    isStageZoomOut,
    isLeftShrinkedStageZoomOut,
    isFocused,
    sectionRef,
    corvidName,
    sectionType: sectionsUtils.getSectionType(editorAPI, sectionRef),
    sectionRect,
    isDragging,
    isResizing: isResizingSel(stateMapperArgs),
    isMobileEditor: editorAPI.isMobileEditor(),
    isMultilingual:
      editorAPI.language.multilingual.isEnabled() &&
      !multilingual.services.utils.currentIsOriginal(editorAPI),
    shouldShowEmptyState: shouldShowEmptyState(
      editorAPI,
      sectionRef,
      isSelectedBox,
      isBlankSection,
      isSectionBothSelectedAndHovered,
      isLeftShrinkedStageZoomOut,
    ),
    isBlankSection,
    isSectionBothSelectedAndHovered,
    shouldDisplayResizeHandle:
      isHoveredBox &&
      !isDragging &&
      !isLeftShrinkedStageZoomOut &&
      !isInSwitchLayoutMode &&
      editorRestrictionsApi.allowed('sections_resize-handler.visible'),
    siteScale,
    stageXOffset,
    shouldHideLabels:
      isHoveredBox &&
      !isStageZoomOut &&
      !editorRestrictionsApi.allowed('sections_section-box-labels.visible') &&
      editorAPI.utils.isSameRef(selectedSection, hoveredSection),
    shouldDisplayPreLoader,
    isInSwitchLayoutMode,
    isOverlayVisible,
    areContainerPointerEventsEnabled,
    stageActionsSkin,
  };
};

export interface SectionBoxDispatchProps {
  logButtonBi: (interactionName: InteractionName) => void;
  openAddPanel: () => void;
  openAddSectionPanel: (
    currentSectionRef: CompRef,
    buttonAlignment: ButtonAlignment,
    origin: 'add-section-button' | 'empty-state-link' | 'section_empty_state',
    category?: string,
  ) => void;
  addBlankSection: (
    currentSectionRef: CompRef,
    buttonAlignment: ButtonAlignment,
  ) => void;
  selectComponent: (compRef: CompRef) => void;
  logEmptyStateShownBi: (options: {
    actionName: ShowEmptyState;
    shownEmptyState: boolean;
  }) => void;
  logEmptyStateButtonClick: (type: EmptyStateButtonClick) => void;
  getMouseY: () => number;
  clearHoveredSection: () => void;
  isAddSectionButtonOverlapsComponent: (
    buttonAlignment: ButtonAlignment,
    sectionRef: CompRef,
  ) => boolean;
}

const getEditorAPI: ThunkAction<EditorAPI> = (
  dispatch,
  getState,
  { editorAPI }: { editorAPI: EditorAPI },
) => editorAPI;

export const mapDispatchToProps: MapDispatchToProps<
  SectionBoxDispatchProps,
  SectionBoxOwnProps
> = (dispatch: Dispatch, ownProps) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);
  const switchLayoutAPI = editorAPI.host.getAPI(SwitchLayoutApiKey);
  const addPresetApi = editorAPI.host.getAPI(AddPresetApiKey);

  const getCompDefaultBiFields = () => {
    const { sectionRef } = getSelection(editorAPI, ownProps);

    return editorAPI.bi.getComponentsBIParams([sectionRef])[0];
  };

  return {
    selectComponent: (compRef) => {
      if (
        switchLayoutAPI.isSwitchLayoutLoading() ||
        switchLayoutAPI.isSwitchLayoutSelecting()
      ) {
        return;
      }

      // TODO: use origin from const/enum
      switchLayoutAPI.reloadResults({
        sectionRef: compRef,
        origin: SelectSectionBiOrigin,
      });

      editorAPI.selection.selectComponentByCompRef(compRef);
    },
    logButtonBi: (interactionName: InteractionName) => {
      biLogger.report(
        addSectionButton({
          actionName: interactionName,
        }),
      );
    },
    logEmptyStateShownBi: ({ actionName, shownEmptyState }) => {
      biLogger.report(
        sectionShowEmptyState({
          ...getCompDefaultBiFields(),
          actionName,
          shownEmptyState,
        }),
      );
    },
    logEmptyStateButtonClick: (type: EmptyStateButtonClick) => {
      biLogger.report(
        sectionEmptyStateClick({
          ...getCompDefaultBiFields(),
          type,
          origin: 'stage',
        }),
      );
    },
    openAddSectionPanel: (
      currentSectionRef,
      buttonAlignment,
      origin,
      category?: string,
    ) => {
      addSectionInteraction.start();
      const state = editorAPI.store.getState();
      const pageSections = editorAPI.sections.getPageSectionsSortedByStageOrder(
        editorAPI.pages.getCurrentPage(),
      );
      const currentIndex = pageSections.findIndex(
        (sectionRef) => sectionRef.id === currentSectionRef.id,
      );

      const getStageEntryIndex = () => {
        switch (origin) {
          case 'add-section-button':
            const isTopButtonClicked = buttonAlignment === ButtonAlignment.TOP;
            return isTopButtonClicked ? currentIndex : currentIndex + 1;
          case 'section_empty_state':
            return currentIndex;
          default:
            return 0;
        }
      };

      const stageEntryIndex = getStageEntryIndex();
      const emptyStateBlankSectionIndex =
        stageEntryIndex + (origin === 'add-section-button' ? 0 : 1);

      const stageLayout = domMeasurements.selectors.getStageLayout(state);
      const sectionLayout =
        editorAPI.components.layout.get_rect(currentSectionRef);
      const yPositionMap = {
        [ButtonAlignment.TOP]: sectionLayout.y,
        [ButtonAlignment.BOTTOM]: sectionLayout.y + sectionLayout.height,
      };

      editorAPI.documentMode.setExtraSiteHeight(UNSCALED_DROPZONE_HEIGHT);

      const maxScrollY =
        Math.max(0, editorAPI.site.getHeight() - stageLayout.height * 2) +
        UNSCALED_DROPZONE_HEIGHT -
        editorAPI.editorConfig.extraSiteHeight;

      const hasScrollHeightInZoomMode =
        editorAPI.site.getHeight() > stageLayout.height / DEFAULT_SITE_SCALE;

      const desiredScrollY = hasScrollHeightInZoomMode
        ? Math.max(
            0,
            yPositionMap[buttonAlignment] -
              stageLayout.height / 2 -
              stageLayout.top -
              UNSCALED_DROPZONE_HEIGHT,
          )
        : 0;

      const shouldReplaceSingleBlankSection = origin === 'section_empty_state';

      ErrorReporter.breadcrumb('opening addSection panel from SectionBox', {
        stageEntryIndex,
        origin,
        buttonAlignment,
      });

      dispatch(
        panels.actions.openLeftPanel(
          constants.ROOT_COMPS.LEFTBAR.ADD_SECTION_PANEL_NAME,
          {
            origin,
            category,
            stageEntryIndex,
            emptyStateBlankSectionIndex,
            shouldReplaceSingleBlankSection,
            emptyStateSectionReplacementRef: currentSectionRef,
            zoomModeParams: {
              originalScrollY: Math.min(desiredScrollY, maxScrollY),
            },
            onClose: () => addSectionInteraction.end(),
            onSectionAddedToStage: () => addSectionInteraction.end(),
          },
        ),
      );
    },
    openAddPanel: () => {
      editorAPI.panelManager.openPanel(
        constants.ROOT_COMPS.LEFTBAR.ADD_PANEL_NAME,
        {
          origin: 'section_empty_state',
        },
      );
    },
    getMouseY: () => editorAPI.cursor.get().y,
    clearHoveredSection: () =>
      dispatch(sectionsState.actions.setHoveredSectionLike(null)),
    isAddSectionButtonOverlapsComponent: (buttonAlignment, sectionRef) =>
      getIsAddSectionButtonOverlapsComponent(
        editorAPI,
        buttonAlignment,
        sectionRef,
      ),
    addBlankSection: (currentSectionRef, buttonAlignment: ButtonAlignment) => {
      const pageSections = editorAPI.sections.getPageSectionsSortedByStageOrder(
        editorAPI.pages.getCurrentPage(),
      );
      const currentIndex = pageSections.findIndex(
        (sectionRef) => sectionRef.id === currentSectionRef.id,
      );
      const stageEntryIndex =
        buttonAlignment === ButtonAlignment.TOP
          ? currentIndex
          : currentIndex + 1;

      addPresetApi.sections.addBlankSection(
        'add-section-button',
        'add_section_button_flow',
        undefined,
        null,
        false,
        stageEntryIndex,
      );
    },
  };
};
